import React from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  PhoneIncoming,
  UserCheck,
  CalendarCheck,
  Brain,
} from "lucide-react";

const AshaBasicallyFreeSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="max-w-[1100px] w-full flex flex-col items-center relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100">
            <TrendingUp className="w-4 h-4" />
            <span>The Bottom Line</span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-3 tracking-tight leading-[1.1]">
            Other platforms are a cost.{" "}
            <span className="text-blue-600">Asha is an investment that pays for itself.</span>
          </h2>
        </motion.div>

        {/* Main point */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-xl text-slate-700 leading-relaxed text-center max-w-[900px] mb-8"
        >
          Most offices stop thinking of Asha as a cost once they get started — because
          the uptick in production it drives{" "}
          <span className="font-bold text-slate-900">pays for the service many times over</span>.
        </motion.p>

        {/* Production stat - hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl border-2 border-blue-200 shadow-lg px-10 py-8 mb-8 text-center"
        >
          <p className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">
            Average monthly production increase
          </p>
          <div className="text-6xl lg:text-7xl font-extrabold text-blue-600 tracking-tight tabular-nums mb-2">
            $9,800<span className="text-2xl font-bold text-slate-400">/mo</span>
          </div>
          <p className="text-base text-slate-500">
            Many offices see <span className="font-bold text-slate-800">$20,000+/mo</span> — starting in the very first month.
          </p>
        </motion.div>

        {/* Sub-label */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-2 mb-3"
        >
          <div className="h-px w-8 bg-slate-300" />
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Where the production comes from
          </span>
          <div className="h-px w-8 bg-slate-300" />
        </motion.div>

        {/* Production drivers — 4 columns */}
        <div className="w-full grid grid-cols-4 gap-4">
          {[
            {
              icon: PhoneIncoming,
              title: "More calls captured",
              desc: "AI answers every call 24/7 — no more missed new patients or after-hours opportunities.",
              color: "blue",
            },
            {
              icon: UserCheck,
              title: "Patients reactivated",
              desc: "AI reaches out to patients who fell off your schedule and brings them back in for care.",
              color: "indigo",
            },
            {
              icon: CalendarCheck,
              title: "Schedule gaps filled",
              desc: "AI fills empty slots via waitlists, confirmations, and proactive outreach — maximizing chair time.",
              color: "violet",
            },
            {
              icon: Brain,
              title: "Higher case acceptance",
              desc: "AI follow-ups drive treatment acceptance with unlimited bandwidth your staff doesn't have.",
              color: "blue",
            },
          ].map((item, i) => {
            const colorStyles = {
              blue: { iconBg: "bg-blue-50", iconBorder: "border-blue-100", iconText: "text-blue-600" },
              indigo: { iconBg: "bg-indigo-50", iconBorder: "border-indigo-100", iconText: "text-indigo-600" },
              violet: { iconBg: "bg-violet-50", iconBorder: "border-violet-100", iconText: "text-violet-600" },
            };
            const c = colorStyles[item.color];

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + i * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`w-9 h-9 rounded-lg ${c.iconBg} ${c.iconBorder} border flex items-center justify-center`}>
                    <item.icon className={`w-4.5 h-4.5 ${c.iconText}`} />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{item.title}</h3>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AshaBasicallyFreeSlide;
